import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SavedOrder } from '../../shared/menu';
import { ApiError, fetchOrders, fetchSavedMenus } from './api';
import { listLocalMenusWithMetadata, type LocalMenuSummary } from './persist';
import { useOrderHistory } from './store';

export interface MenuListEntry {
  menuId: string;
  restaurantName: string | null;
  sourceLanguage: string;
  targetLanguage: string;
  savedAt: string;
  // First pageImage when known (from local IDB). Server-pinned menus don't
  // expose this in the saved-list response, so MenuListItem falls back to
  // the deterministic /api/menu/:id/page/0 URL with an onError handler.
  thumbnailRef: string | null;
}

interface SavedMenuList {
  list: MenuListEntry[];
  loading: boolean;
  // True when the server fetch returned 401 — caller can offer a sign-in CTA
  // and we still surface local IDB menus underneath.
  isUnauthorized: boolean;
}

// Unified menu list used by Home and Me. Server-pinned menus (signed-in users
// only) take precedence on conflict; local IDB menus fill in for everything
// scanned on this device. Sorted newest first.
export function useSavedMenus(): SavedMenuList {
  const savedQuery = useQuery({ queryKey: ['saved'], queryFn: fetchSavedMenus, retry: false });
  const localMenusQ = useQuery({ queryKey: ['local-menus'], queryFn: listLocalMenusWithMetadata, staleTime: 30_000 });

  const isUnauthorized = savedQuery.error instanceof ApiError && savedQuery.error.status === 401;

  const list = useMemo<MenuListEntry[]>(() => {
    const serverList = savedQuery.data?.saved ?? [];
    const localList: LocalMenuSummary[] = localMenusQ.data ?? [];
    const byId = new Map<string, MenuListEntry>();
    for (const m of localList) byId.set(m.menuId, m);
    for (const m of serverList) {
      // Preserve the local thumbnailRef if we already had one — server payload
      // doesn't include pageImages so a pure server overwrite would lose it.
      const existingThumb = byId.get(m.menuId)?.thumbnailRef ?? null;
      byId.set(m.menuId, {
        menuId: m.menuId,
        restaurantName: m.restaurantName,
        sourceLanguage: m.sourceLanguage,
        targetLanguage: m.targetLanguage,
        savedAt: m.savedAt,
        thumbnailRef: existingThumb,
      });
    }
    return Array.from(byId.values()).sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
  }, [savedQuery.data, localMenusQ.data]);

  const loading = (savedQuery.isLoading && !isUnauthorized) || localMenusQ.isLoading;

  return { list, loading, isUnauthorized };
}

interface SavedOrderList {
  list: SavedOrder[];
  loading: boolean;
}

// Unified order list used by Home and Me. Local-store orders are
// authoritative for display (server orders get hydrated into the local store
// on Me mount); finalized orders, newest first.
export function useSavedOrders(): SavedOrderList {
  const localOrders = useOrderHistory((s) => s.orders);
  const ordersQuery = useQuery({ queryKey: ['orders'], queryFn: fetchOrders, retry: false });

  const list = useMemo<SavedOrder[]>(() => {
    return [...localOrders].sort((a, b) => (a.finalizedAt < b.finalizedAt ? 1 : -1));
  }, [localOrders]);

  return { list, loading: ordersQuery.isLoading };
}
